import { useState } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Wrench } from "@phosphor-icons/react";
import { toast } from "sonner";

// Shown (via App routing) when the authenticated user has must_change_password=true
// after an administrator issued them a temporary password. They cannot use the app
// until they set a new password.
export default function ForcePasswordChange() {
  const { logout, reloadProfile } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) return setErr("Use at least 8 characters.");
    if (password !== confirm) return setErr("Passwords do not match.");
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setBusy(false);
      return setErr("Could not update password. Please sign in again and retry.");
    }
    try {
      await api.post("/me/password-changed");
      await reloadProfile();
      toast.success("Password updated");
    } catch (_) {
      setErr("Password changed but we couldn't finalise. Please sign in again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 bg-blue-600 flex items-center justify-center rounded">
            <Wrench size={28} weight="bold" className="text-white" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-black text-white tracking-tight leading-none">TMIC OPS</h1>
            <p className="font-body text-sm text-slate-400">Operations & Compliance</p>
          </div>
        </div>
        <form onSubmit={submit} className="bg-white p-6 rounded-lg border-2 border-slate-300 flex flex-col gap-4" data-testid="force-change-form">
          <h2 className="font-display text-xl font-bold text-slate-900">Choose a new password</h2>
          <p className="font-body text-sm text-slate-600">
            Your account is using a temporary password. Please set a new password to continue.
          </p>
          <div>
            <label className="font-body text-sm font-semibold text-slate-700">New password</label>
            <Input data-testid="force-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="h-12 text-base border-2 mt-1" required minLength={8} autoComplete="new-password" />
          </div>
          <div>
            <label className="font-body text-sm font-semibold text-slate-700">Confirm new password</label>
            <Input data-testid="force-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="h-12 text-base border-2 mt-1" required minLength={8} autoComplete="new-password" />
          </div>
          {err && <p data-testid="force-error" className="text-sm text-red-600 font-semibold">{err}</p>}
          <Button data-testid="force-submit" type="submit" disabled={busy}
            className="h-12 text-base font-bold uppercase tracking-wide bg-slate-900 hover:bg-slate-800">
            {busy ? "Updating…" : "Set new password"}
          </Button>
          <button type="button" data-testid="force-logout" onClick={logout} className="text-slate-500 font-semibold text-sm">
            Sign out
          </button>
        </form>
      </div>
    </div>
  );
}
